import {
  CustomColumnHeader,
  CustomColumnItem,
  ID,
  LineItemChanges,
  Lot,
  LotChanges,
  getColumnHeaderDataTypes,
} from 'types';
import {
  createDefaultColumnValue,
  createDefaultDisplayTextForDataType,
  updateChanges,
  applyChanges,
  matchesNew,
} from '@components/bid_request/details/utils';

const getNumericDataType = () => getColumnHeaderDataTypes().NUMERIC;

const shouldSetDefault = (
  isDataTypeChanged: boolean,
  dataType: string,
  currentDisplayText?: string,
) => {
  if (!isDataTypeChanged) return false;

  // When changing to numeric, only reset if current value is not a valid number
  if (dataType === getNumericDataType()) {
    return Number.isNaN(Number(currentDisplayText));
  }

  // When changing to text, never reset (preserve existing values)
  return false;
};

interface SetDefaultsForCustomColumnDataTypesParams {
  changes: Record<ID, LotChanges>;
  dataType: string;
  headerId: ID;
  isDataTypeChanged: boolean;
  lotId: ID;
  lots?: Lot[];
}

/**
 * Sets default values for custom column data types when creating a custom column.
 * When creating a custom column, if the data type is not 'text', it will create a default value
 * for each line item in the lot.
 *
 * @param props - params object
 * @param props.changes {Record<ID, LotChanges>} - Current changes to the lots
 * @param props.headerId {ID} - ID of the custom column header
 * @param props.isDataTypeChanged {boolean} - Whether the data type of the custom column has changed
 * @param props.lotId {ID} - ID of the lot to update
 * @param props.lots {Lot[]} - List of lots to find the lot by ID
 * @returns {Record<ID, LotChanges>} Updated changes with defaults set for custom column data types.
 */
export const setDefaultsForCustomColumnDataTypes = ({
  changes,
  dataType,
  headerId,
  isDataTypeChanged,
  lotId,
  lots,
}: SetDefaultsForCustomColumnDataTypesParams): Record<ID, LotChanges> => {
  let lot = lots?.find(lt => lt.id === lotId);

  // If not found, use applyChanges to get the proper lot structure
  if (!lot && changes[lotId]) {
    const appliedLots = applyChanges({ lotChanges: changes, lots: lots || [] });
    lot = appliedLots.find(l => l.id === lotId);
  }
  
  const newChanges = { ...changes };
  const newLotChanges = newChanges[lotId] || ({} as LotChanges);
  const headerChanges = newLotChanges.custom_column_headers?.[headerId] ||
    ({ id: headerId, lot_id: lotId } as CustomColumnHeader);
  if (!lot) return newChanges;

  let newLineItemsChanges = newLotChanges.line_items ||
    ({} as Record<ID, LineItemChanges>);

  lot.line_items.forEach(lineItem => {
    const lineItemId = lineItem.id;
    const lineItemChanges = newLineItemsChanges[lineItemId] ||
      ({ id: lineItemId, lot_id: lotId } as LineItemChanges);
    const customColumnItemsChanges = lineItemChanges.custom_column_items ||
      ({} as Record<ID, CustomColumnItem>);
    const original = lineItem.custom_column_items
      ?.find(item => (
        item.custom_column_header_id === headerId
      ));

    let customColumnItemChanges;
    if (original) {
      const existingCustomColumnItem = customColumnItemsChanges[original.id];

      if (existingCustomColumnItem) {
        customColumnItemChanges = {
          ...existingCustomColumnItem,
          is_supplier_visible: headerChanges.is_supplier_visible ?? original.is_supplier_visible,
        };
      } else {
        customColumnItemChanges = {
          custom_column_header_id: headerId,
          id: original.id,
          line_item_id: lineItemId,
        } as CustomColumnItem;
      }
    } else {
      customColumnItemChanges = Object.values(customColumnItemsChanges)
        .find(item => (
          item.custom_column_header_id === headerId
        ));
      if (!customColumnItemChanges) {
        customColumnItemChanges = createDefaultColumnValue(lineItemId, headerChanges);
      }
    }

    const id = customColumnItemChanges?.id || original?.id || '';
    const currentDisplayText = customColumnItemChanges?.display_text ?? original?.display_text;
    const updates = {
      display_text: shouldSetDefault(isDataTypeChanged, dataType, currentDisplayText) ?
        createDefaultDisplayTextForDataType(dataType) :
        currentDisplayText,
    };
    // Only preserve display_text for new items when we're not resetting it due to shouldSetDefault logic
    const shouldReset = shouldSetDefault(isDataTypeChanged, dataType, currentDisplayText);
    const shouldPreserveDisplayText = (!original || matchesNew(String(id))) && !shouldReset;
    
    const updatedItem = updateChanges({
      changes: customColumnItemChanges,
      original,
      preserveFields: shouldPreserveDisplayText ? ['display_text'] : [],
      updates,
    }) as CustomColumnItem;
    
    newLineItemsChanges = {
      ...newLineItemsChanges,
      [lineItemId]: {
        ...lineItemChanges,
        custom_column_items: {
          ...customColumnItemsChanges,
          [id]: updatedItem,
        },
      },
    };
  });

  newChanges[lotId] = { ...newLotChanges, line_items: { ...newLineItemsChanges } };

  return newChanges;
};
